import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { crmService, customerService } from '../services/api';
import { retryOnServerError } from '../components/dashboard/queryOptions';
import { botsOf } from '../pages/dashboard/integrations/integrationStatus';

// Catálogo do crm_service com as configs do cliente; lista e detalhe usam a mesma chave.
export const useIntegrations = () =>
  useQuery({
    queryKey: ['integrations'],
    queryFn: () => crmService.listIntegrations().then((res) => res.data.integrations),
    retry: retryOnServerError,
  });

// Linha completa (crm_config / stock_config), sem as credenciais, que são write-only.
export const useIntegrationConfig = (type, id) =>
  useQuery({
    queryKey: ['integrationConfig', type, id],
    queryFn: () => crmService.getIntegrationConfig(type, id).then((res) => res.data),
    enabled: Boolean(id),
    retry: retryOnServerError,
  });

// Log de uma linha de config: export_attempts no CRM, stock_sync_attempts no estoque.
export const useIntegrationLogs = (type, configId, params) =>
  useQuery({
    queryKey: ['integrationLogs', type, configId, params],
    queryFn: () => {
      const request = type === 'crm'
        ? crmService.listExportAttempts({ ...params, crm_config_id: configId })
        : crmService.listStockSyncAttempts({ ...params, stock_config_id: configId });
      return request.then((res) => res.data);
    },
    enabled: Boolean(configId),
    placeholderData: keepPreviousData,
    retry: retryOnServerError,
  });

// Agentes das lojas do cliente (ver botsOf): o bot_id de uma integração de estoque é um deles.
// `customer` só vale para usuários internos, que senão veriam as lojas de todos os clientes.
export const useBotStores = (customerUuid) =>
  useQuery({
    queryKey: ['businesses', 'integrations', customerUuid],
    queryFn: () => customerService.listBusinesses({ customer: customerUuid }).then((res) => res.data),
    select: botsOf,
    enabled: Boolean(customerUuid),
    retry: retryOnServerError,
  });

// Invalida `queryKeys` antes do onSuccess de quem chamou.
const useInvalidatingMutation = (mutationFn, queryKeys, options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn,
    onSuccess: (...args) => {
      queryKeys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
      options.onSuccess?.(...args);
    },
  });
};

// Sem `id` cria; com `id`, payload vazio não faz requisição (nada mudou).
export const useSaveIntegrationConfig = (type, id, options) =>
  useInvalidatingMutation(
    (payload) => {
      if (!id) return crmService.createIntegrationConfig(type, payload);
      if (Object.keys(payload).length === 0) return Promise.resolve();
      return crmService.updateIntegrationConfig(type, id, payload);
    },
    [['integrations'], ['integrationConfig', type, id]],
    options,
  );

export const useToggleIntegrationConfig = (type, id, options) =>
  useInvalidatingMutation(
    (enabled) => crmService.updateIntegrationConfig(type, id, { enabled }),
    [['integrations'], ['integrationConfig', type, id]],
    options,
  );

// Soft-delete: a lista oferece o desfazer.
export const useDeleteIntegrationConfig = (type, id, options) =>
  useInvalidatingMutation(() => crmService.deleteIntegrationConfig(type, id), [['integrations']], options);

export const useAuthorizeDealerspace = (configId, options) =>
  useInvalidatingMutation((code) => crmService.authorizeDealerspace(configId, code), [['integrations']], options);

// CRM: reenvia a tentativa; estoque: força uma sincronização da linha.
export const useRetryIntegrationLog = (type, configId, options) =>
  useInvalidatingMutation(
    (row) =>
      (type === 'crm' ? crmService.retryExportAttempt(row.id) : crmService.syncStockConfig(configId)).then((res) => res.data),
    [['integrationLogs', type, configId], ['integrations']],
    options,
  );
